import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Calculator, TrendingUp, DollarSign, Zap, ArrowRight } from 'lucide-react';
import { turbineModels, financialData } from '../mock';

const FinancialCalculator = () => {
  const [selectedModel, setSelectedModel] = useState(turbineModels[0]);
  const [numUnits, setNumUnits] = useState(1);
  const [location, setLocation] = useState('uk');
  const [windSpeed, setWindSpeed] = useState(7);
  const [results, setResults] = useState(null);

  const calculateROI = () => {
    const electricityPrice = financialData.electricityPrices[location];
    const adjustedOutput = selectedModel.annualOutput * (windSpeed / 8.5); // Adjust for wind speed
    const annualRevenue = adjustedOutput * 1000 * electricityPrice * numUnits;
    const totalInvestment = selectedModel.installedCost * numUnits;
    const annualMaintenance = financialData.maintenanceCostPerYear * numUnits;
    const netAnnualCashFlow = annualRevenue - annualMaintenance;
    const paybackYears = totalInvestment / netAnnualCashFlow;
    const tenYearRevenue = annualRevenue * 10;
    const tenYearMaintenance = annualMaintenance * 10;
    const tenYearProfit = tenYearRevenue - tenYearMaintenance - totalInvestment;
    const roi = (tenYearProfit / totalInvestment) * 100;
    
    // Calculate NPV
    let npv = -totalInvestment;
    for (let year = 1; year <= 25; year++) {
      npv += netAnnualCashFlow / Math.pow(1 + financialData.discountRate, year);
    }
    
    setResults({
      annualRevenue,
      netAnnualCashFlow,
      paybackYears,
      tenYearProfit,
      roi,
      npv,
      totalInvestment,
      annualMaintenance,
      adjustedOutput: adjustedOutput * numUnits
    });
  };

  return (
    <div className="calculator-page">
      {/* Header */}
      <section className="page-header">
        <div className="container">
          <div className="header-content">
            <div className="header-icon">
              <Calculator size={48} />
            </div>
            <h1 className="page-title">Financial ROI Calculator</h1>
            <p className="page-subtitle">
              Calculate your return on investment with precise wind speed and location data
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="calculator-section">
        <div className="container">
          <div className="calculator-layout">
            {/* Input Panel */}
            <Card className="calculator-inputs">
              <div className="calculator-header">
                <h2>Configure Your System</h2>
                <p>Adjust parameters to see real-time ROI calculations</p>
              </div>
              
              <div className="input-group">
                <Label htmlFor="turbine-model">Turbine Model</Label>
                <Select 
                  value={selectedModel.id} 
                  onValueChange={(value) => {
                    const model = turbineModels.find(m => m.id === value);
                    setSelectedModel(model);
                  }}
                >
                  <SelectTrigger id="turbine-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {turbineModels.map(model => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name} ({model.power}kW)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="model-info">
                  <span>Annual Output: {selectedModel.annualOutput} MWh</span>
                  <span>Installed Cost: €{selectedModel.installedCost.toLocaleString()}</span>
                </div>
              </div>

              <div className="input-group">
                <Label htmlFor="location">Location / Electricity Price</Label>
                <Select value={location} onValueChange={setLocation}>
                  <SelectTrigger id="location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uk">UK (€0.28/kWh)</SelectItem>
                    <SelectItem value="germany">Germany (€0.32/kWh)</SelectItem>
                    <SelectItem value="france">France (€0.21/kWh)</SelectItem>
                    <SelectItem value="spain">Spain (€0.25/kWh)</SelectItem>
                    <SelectItem value="usa">USA (€0.16/kWh)</SelectItem>
                    <SelectItem value="global-avg">Global Average (€0.22/kWh)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="input-group">
                <Label htmlFor="units">Number of Units: {numUnits}</Label>
                <Slider
                  id="units"
                  min={1}
                  max={100}
                  step={1}
                  value={[numUnits]}
                  onValueChange={(value) => setNumUnits(value[0])}
                  className="mt-2"
                />
                <div className="slider-info">
                  <span>1 unit</span>
                  <span>100 units</span>
                </div>
              </div>

              <div className="input-group">
                <Label htmlFor="wind-speed">Average Wind Speed: {windSpeed} m/s</Label>
                <Slider
                  id="wind-speed"
                  min={4}
                  max={15}
                  step={0.5}
                  value={[windSpeed]}
                  onValueChange={(value) => setWindSpeed(value[0])}
                  className="mt-2"
                />
                <div className="slider-info">
                  <span>4 m/s</span>
                  <span>15 m/s</span>
                </div>
              </div>

              <Button 
                size="lg" 
                className="calculate-btn"
                onClick={calculateROI}
              >
                <Calculator className="mr-2" />
                Calculate ROI
              </Button>
            </Card>

            {/* Results Panel */}
            {results && (
              <Card className="calculator-results">
                <div className="results-header">
                  <h2>Financial Analysis</h2>
                  <div className="results-badge">
                    {results.paybackYears < 6 ? 'Excellent ROI' : 'Good ROI'}
                  </div>
                </div>

                <div className="results-grid">
                  <div className="result-card primary">
                    <div className="result-icon">
                      <TrendingUp />
                    </div>
                    <div className="result-content">
                      <div className="result-label">Payback Period</div>
                      <div className="result-value">{results.paybackYears.toFixed(1)} years</div>
                    </div>
                  </div>

                  <div className="result-card">
                    <div className="result-icon">
                      <DollarSign />
                    </div>
                    <div className="result-content">
                      <div className="result-label">Total Investment</div>
                      <div className="result-value">€{results.totalInvestment.toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="result-card">
                    <div className="result-icon">
                      <Zap />
                    </div>
                    <div className="result-content">
                      <div className="result-label">Annual Revenue</div>
                      <div className="result-value">€{Math.round(results.annualRevenue).toLocaleString()}</div>
                    </div>
                  </div>

                  <div className="result-card">
                    <div className="result-icon">
                      <TrendingUp />
                    </div>
                    <div className="result-content">
                      <div className="result-label">Net Annual Cash Flow</div>
                      <div className="result-value">€{Math.round(results.netAnnualCashFlow).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div className="results-detailed">
                  <h3>10-Year Financial Projection</h3>
                  <div className="detailed-metrics">
                    <div className="metric-row">
                      <span className="metric-label">Total 10-Year Profit</span>
                      <span className="metric-value success">€{Math.round(results.tenYearProfit).toLocaleString()}</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Return on Investment (ROI)</span>
                      <span className="metric-value success">{results.roi.toFixed(1)}%</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Net Present Value (NPV)</span>
                      <span className="metric-value">€{Math.round(results.npv).toLocaleString()}</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Annual Energy Production</span>
                      <span className="metric-value">{results.adjustedOutput.toFixed(1)} MWh</span>
                    </div>
                    <div className="metric-row">
                      <span className="metric-label">Annual Maintenance</span>
                      <span className="metric-value">€{results.annualMaintenance.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="results-chart">
                  <h3>Cash Flow Projection (Years 1-10)</h3>
                  <div className="chart-bars">
                    {[...Array(10)].map((_, year) => {
                      const yearCashFlow = results.netAnnualCashFlow;
                      const height = Math.min((yearCashFlow / results.annualRevenue) * 100, 100);
                      return (
                        <div key={year} className="chart-bar-container">
                          <div 
                            className="chart-bar" 
                            style={{ height: `${height}%` }}
                          >
                            <span className="bar-value">€{Math.round(yearCashFlow / 1000)}k</span>
                          </div>
                          <span className="bar-label">Y{year + 1}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="results-cta">
                  <p>Ready to deploy? Our team can help with site assessment and installation.</p>
                  <Button size="lg" className="cta-primary">
                    Contact Sales Team <ArrowRight className="ml-2" />
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Unit Economics Section */}
      <section className="economics-section">
        <div className="container">
          <h2 className="section-title">Unit Economics</h2>
          <div className="economics-grid">
            {turbineModels.map(model => {
              const electricityPrice = financialData.electricityPrices['global-avg'];
              const annualRevenue = model.annualOutput * 1000 * electricityPrice;
              const netAnnual = annualRevenue - financialData.maintenanceCostPerYear;
              const payback = model.installedCost / netAnnual;
              const tenYearProfit = (netAnnual * 10) - model.installedCost;
              
              return (
                <Card key={model.id} className="economics-card">
                  <h3>{model.name}</h3>
                  <div className="economics-metrics">
                    <div className="econ-metric">
                      <span className="econ-label">Installed Cost</span>
                      <span className="econ-value">€{model.installedCost.toLocaleString()}</span>
                    </div>
                    <div className="econ-metric">
                      <span className="econ-label">Annual Revenue</span>
                      <span className="econ-value">€{Math.round(annualRevenue).toLocaleString()}</span>
                    </div>
                    <div className="econ-metric">
                      <span className="econ-label">Payback Period</span>
                      <span className="econ-value">{payback.toFixed(1)} years</span>
                    </div>
                    <div className="econ-metric">
                      <span className="econ-label">10-Year Net Cash</span>
                      <span className="econ-value success">€{Math.round(tenYearProfit).toLocaleString()}</span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancialCalculator;
